import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChatInterface } from './ChatInterface';

interface AnimatedDashboardProps {
  className?: string;
}

export const AnimatedDashboard: React.FC<AnimatedDashboardProps> = ({
  className,
}) => {
  const [, setAnimationPhase] = useState(0);
  const [showChartClick, setShowChartClick] = useState(false);
  const [, setShowClickIndicator] = useState(true);
  const [isChartHighlighted, setIsChartHighlighted] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showBusinessOverview, setShowBusinessOverview] = useState(true);

  // Start animation sequence after component loads
  useEffect(() => {
    const timer = setTimeout(() => {
      startAnimation();
    }, 1000); // Wait 1 second before starting

    return () => clearTimeout(timer);
  }, []);

  const startAnimation = async () => {
    // Phase 1: Initial wait (slower for user comprehension)
    setAnimationPhase(1);
    await wait(1000);

    // Phase 2: Simulate click - hide indicator and start click effect
    setShowClickIndicator(false);
    setShowChartClick(true);
    setAnimationPhase(2);
    await wait(600);

    // Phase 3: Highlight chart (slower)
    setShowChartClick(false);
    setIsChartHighlighted(true);
    setAnimationPhase(3);
    await wait(800);

    // Phase 4-5: Hide business overview and show chat simultaneously
    setShowBusinessOverview(false);
    setShowChat(true);
    setAnimationPhase(4);
    await wait(600);

    // Phase 5: Let ChatInterface handle its animations
    setAnimationPhase(5);

    // Wait for chat sequence to complete (about 11.8 seconds)
    await wait(12000);

    // Phase 6: Reset chart, fade out chat, and bring back business overview simultaneously
    setAnimationPhase(6);
    setIsChartHighlighted(false); // Reset chart highlighting
    setShowChat(false); // Fade out chat
    setShowBusinessOverview(true); // Bring back business overview

    await wait(600); // Wait for all transitions to complete

    // Final reset of remaining states
    setShowChartClick(false);
    setShowClickIndicator(true);
    setAnimationPhase(0);

    // Wait a moment before restarting
    await wait(1000);

    // Loop the animation
    startAnimation();
  };

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Animation variants (cardVariants removed - using static white background)

  const chartSectionVariants = {
    normal: {
      scale: 1,
      y: 0,
      opacity: 1,
      boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    clicked: {
      scale: 0.98,
      opacity: 0.8,
      transition: { duration: 0.15, ease: 'easeOut' },
    },
    highlighted: {
      scale: 1.02,
      y: -8,
      opacity: 1,
      boxShadow:
        '0 20px 60px rgba(147, 51, 234, 0.15), 0 8px 32px rgba(168, 85, 247, 0.12)',
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  // Get chart animation state
  const getChartAnimationState = () => {
    if (showChartClick) return 'clicked';
    if (isChartHighlighted) return 'highlighted';
    return 'normal';
  };

  return (
    <div
      className={`group relative h-full ${className}`}
      style={{
        contain: 'layout style', // Prevent layout changes from affecting parent
      }}
    >
      {/* Glassmorphism Card */}
      <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/30 bg-white/60 p-8 shadow-[0_4px_6px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-[8px] backdrop-saturate-180 transition-all duration-500 max-[599px]:p-4 lg:p-10">
        {/* Gradient overlays */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-600/15 via-transparent to-transparent"></div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-tl from-blue-600/12 via-transparent to-transparent"></div>

        {/* Content */}
        <div className="relative z-10 flex h-full flex-col">
          <div className="mb-8 max-[599px]:text-center">
            {/* Desktop: Single line heading */}
            <h3 className="text-2xl font-semibold text-slate-900 max-[599px]:hidden">
              Dashboard that you can talk to
            </h3>
            {/* Mobile: Main heading + subheading structure */}
            <div className="hidden max-[599px]:block">
              <h3 className="mb-2 text-3xl font-bold text-slate-900">
                Dashboards
              </h3>
              <p className="text-sm text-slate-600">that you can talk to</p>
            </div>
          </div>

          {/* Dashboard Interface */}
          <div className="h-full w-full flex-1">
            {/* Animated Dashboard Card - Original Height */}
            <motion.div
              className="h-[750px] overflow-hidden rounded-2xl border border-white/50 bg-white px-6 pt-10 pb-6 max-[599px]:h-[500px] max-[599px]:px-4 max-[599px]:pt-6 max-[599px]:pb-4"
              style={{ overscrollBehavior: 'contain' }}
            >
              {/* Chart Section with Click and Highlighting */}
              <motion.div
                variants={chartSectionVariants}
                animate={getChartAnimationState()}
                className="mb-4 rounded-2xl bg-white/95 p-4 max-[599px]:mb-3 max-[599px]:p-3"
              >
                {/* Monthly Expenditure Chart */}
                <div className="mb-6 flex items-start justify-between max-[599px]:mb-4">
                  <div>
                    <p className="text-lg font-semibold text-gray-700 max-[599px]:text-base">
                      Monthly Expenditure
                    </p>
                    <div className="mt-3 flex items-center space-x-4 max-[599px]:mt-2 max-[599px]:space-x-3">
                      <div className="flex items-center space-x-2">
                        <div className="h-3 w-3 rounded-full bg-blue-500 max-[599px]:h-2.5 max-[599px]:w-2.5"></div>
                        <span className="text-sm font-medium text-gray-700 max-[599px]:text-xs">
                          Budget
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="h-3 w-3 rounded-full bg-purple-500 max-[599px]:h-2.5 max-[599px]:w-2.5"></div>
                        <span className="text-sm font-medium text-gray-700 max-[599px]:text-xs">
                          Expense
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chart with Click Indicator */}
                <div className="relative mb-3 h-64 max-[599px]:mb-2 max-[599px]:h-40">
                  <svg
                    className="absolute inset-0 h-full w-full"
                    preserveAspectRatio="none"
                    viewBox="0 0 360 150"
                  >
                    <defs>
                      <linearGradient
                        id="blueGradientAnimated"
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#3B82F6"
                          stopOpacity="0.3"
                        ></stop>
                        <stop
                          offset="100%"
                          stopColor="#3B82F6"
                          stopOpacity="0"
                        ></stop>
                      </linearGradient>
                      <linearGradient
                        id="purpleGradientAnimated"
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#8B5CF6"
                          stopOpacity="0.3"
                        ></stop>
                        <stop
                          offset="100%"
                          stopColor="#8B5CF6"
                          stopOpacity="0"
                        ></stop>
                      </linearGradient>
                    </defs>

                    {/* Grid lines */}
                    <line
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      x1="0"
                      x2="360"
                      y1="140"
                      y2="140"
                    ></line>
                    <line
                      stroke="#e5e7eb"
                      strokeDasharray="2 2"
                      strokeWidth="1"
                      x1="0"
                      x2="360"
                      y1="105"
                      y2="105"
                    ></line>
                    <line
                      stroke="#e5e7eb"
                      strokeDasharray="2 2"
                      strokeWidth="1"
                      x1="0"
                      x2="360"
                      y1="70"
                      y2="70"
                    ></line>
                    <line
                      stroke="#e5e7eb"
                      strokeDasharray="2 2"
                      strokeWidth="1"
                      x1="0"
                      x2="360"
                      y1="35"
                      y2="35"
                    ></line>
                    <line
                      stroke="#e5e7eb"
                      strokeDasharray="2 2"
                      strokeWidth="1"
                      x1="0"
                      x2="360"
                      y1="0"
                      y2="0"
                    ></line>

                    {/* Budget line */}
                    <path
                      d="M0 100 L60 101.4 L120 101.4 L180 80 L240 80 L300 80 L360 60 V150 H0 Z"
                      fill="url(#blueGradientAnimated)"
                    ></path>
                    <path
                      d="M0 100 L60 101.4 L120 101.4 L180 80 L240 80 L300 80 L360 60"
                      fill="none"
                      stroke="#3B82F6"
                      strokeWidth="2"
                    ></path>

                    {/* Expense line with peak at May (x=240) */}
                    <path
                      d="M0 103.8 L60 119.8 L120 117.3 L180 71 L240 10 L300 85 L360 62.5 V150 H0 Z"
                      fill="url(#purpleGradientAnimated)"
                    ></path>
                    <path
                      d="M0 103.8 L60 119.8 L120 117.3 L180 71 L240 10 L300 85 L360 62.5"
                      fill="none"
                      stroke="#8B5CF6"
                      strokeWidth="2"
                    ></path>
                  </svg>
                </div>

                {/* Month Labels */}
                <div className="flex items-center justify-around text-sm font-medium text-gray-500 max-[599px]:justify-between max-[599px]:px-1 max-[599px]:text-xs">
                  <span>JAN</span>
                  <span>FEB</span>
                  <span>MAR</span>
                  <span>APR</span>
                  <span>MAY</span>
                  <span>JUN</span>
                  <span className="rounded-full bg-gray-800 px-3 py-1 text-center text-white max-[599px]:px-2 max-[599px]:py-0.5">
                    JUL
                  </span>
                </div>
              </motion.div>

              {/* Fixed Container - Always same height, prevents scroll jumps */}
              <div className="relative h-[350px] overflow-hidden max-[599px]:h-[220px]">
                {/* Business Overview - Absolute positioned */}
                <motion.div
                  className="absolute inset-0"
                  animate={{
                    opacity: showBusinessOverview ? 1 : 0,
                    pointerEvents: showBusinessOverview ? 'auto' : 'none',
                  }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                >
                  <div className="h-full">
                    <div className="mb-3 flex items-center justify-between max-[599px]:mb-2">
                      <h2 className="text-lg font-bold text-gray-800 max-[599px]:text-base">
                        Business Overview
                      </h2>
                    </div>
                    <div className="grid grid-cols-2 gap-3 max-[599px]:gap-2">
                      <div className="rounded-xl border border-green-200/50 bg-green-50/80 p-5 shadow-sm backdrop-blur-sm max-[599px]:p-3">
                        <p className="mb-2 text-sm font-semibold text-green-800 max-[599px]:mb-1 max-[599px]:text-xs">
                          Total Revenue
                        </p>
                        <p className="mb-1 text-2xl font-bold text-green-900 max-[599px]:mb-0.5 max-[599px]:text-lg">
                          $416,667
                        </p>
                        <p className="mb-4 text-sm text-gray-500 max-[599px]:mb-2 max-[599px]:text-xs">
                          July
                        </p>
                        <div className="h-16 max-[599px]:h-10">
                          <svg
                            className="h-full w-full"
                            viewBox="0 0 100 50"
                            preserveAspectRatio="none"
                          >
                            <path
                              d="M 0 40 C 20 40, 20 10, 40 10 S 60 20, 80 20 S 100 5, 100 5"
                              fill="none"
                              stroke="#10B981"
                              strokeWidth="3"
                            ></path>
                          </svg>
                        </div>
                      </div>
                      <div className="rounded-xl border border-red-200/50 bg-red-50/80 p-5 shadow-sm backdrop-blur-sm max-[599px]:p-3">
                        <p className="mb-2 text-sm font-semibold text-red-800 max-[599px]:mb-1 max-[599px]:text-xs">
                          Total Expenditure
                        </p>
                        <p className="mb-1 text-2xl font-bold text-red-900 max-[599px]:mb-0.5 max-[599px]:text-lg">
                          $245,011
                        </p>
                        <p className="mb-4 text-sm text-gray-500 max-[599px]:mb-2 max-[599px]:text-xs">
                          July
                        </p>
                        <div className="h-16 max-[599px]:h-10">
                          <svg
                            className="h-full w-full"
                            viewBox="0 0 100 50"
                            preserveAspectRatio="none"
                          >
                            <path
                              d="M 0 10 C 20 10, 20 40, 40 40 S 60 20, 80 20 S 100 30, 100 30"
                              fill="none"
                              stroke="#EF4444"
                              strokeWidth="3"
                            ></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Chat Interface - Absolute positioned, centered vertically */}
                <motion.div
                  className="absolute inset-0"
                  animate={{
                    opacity: showChat ? 1 : 0,
                    pointerEvents: showChat ? 'auto' : 'none',
                  }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                >
                  <div className="flex h-full items-center justify-center">
                    {showChat && <ChatInterface />}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
